import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { Search, XCircle, Video, MapPin, Check, ChevronDown, User } from 'lucide-react';
import Button from '../common/Button/Button';
import PermissionGate from '../common/PermissionGate';
import { API_BASE_URL } from '../../api/config';
import interviewService from '../../api/services/interviewService';
import locationService from '../../api/services/locationService';
import { useAuth } from '../../context/AuthContext';
import { formatRoleName } from '../../utils/constants';

/**
 * ScheduleInterviewModal - Reusable component for scheduling interviews
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSuccess - Callback when interview is successfully scheduled
 * @param {Array} props.candidates - Array of candidate objects
 * @param {Object} props.preselectedCandidate - Optional preselected candidate object
 * @param {Function} props.validateField - Optional custom validation function
 */
const ScheduleInterviewModal = ({
    isOpen,
    onClose,
    onSuccess,
    candidates = [],
    preselectedCandidate = null,
    validateField: externalValidateField = null,
    editData = null,
}) => {
    // Auth context
    const { user } = useAuth();

    // State management
    const [formData, setFormData] = useState({
        candidateId: '',
        interviewerId: '',
        interviewerIds: [],
        date: '',
        time: '',
        duration: 60,
        type: 'video',
        location: '',
        meetingLink: '',
        notes: '',
        roundType: '', // Added for multi-round support
        showInterviewerToCandidate: true,
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [interviewerSearchTerm, setInterviewerSearchTerm] = useState('');
    const [debouncedInterviewerSearch, setDebouncedInterviewerSearch] = useState('');
    const [showInterviewerDropdown, setShowInterviewerDropdown] = useState(false);
    const [selectedInterviewers, setSelectedInterviewers] = useState([]);
    const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
    const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
    const [defaultInterviewers, setDefaultInterviewers] = useState([]);
    const [isLoadingDefaultInterviewers, setIsLoadingDefaultInterviewers] = useState(false);
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);

    const candidateDropdownRef = useRef(null);
    const interviewerDropdownRef = useRef(null);
    const locationDropdownRef = useRef(null);

    const interviewTypes = [
        { id: 'video', name: 'Video Call', icon: Video },
        { id: 'in_person', name: 'In Person', icon: MapPin },
    ];

    // Debounce interviewer search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedInterviewerSearch(interviewerSearchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [interviewerSearchTerm]);

    // React Query: Search Employees for Interviewer
    const { data: searchedInterviewersData, isLoading: isLoadingInterviewerSearch } = useQuery({
        queryKey: ['schedule-interview-employees-search', debouncedInterviewerSearch],
        queryFn: async () => {
            if (!debouncedInterviewerSearch || !debouncedInterviewerSearch.trim()) {
                return [];
            }

            const baseUrl = API_BASE_URL;
            const url = `${baseUrl}/hr/employees?search=${encodeURIComponent(debouncedInterviewerSearch.trim())}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();

                if (data.responseCode === 200 && data.data && data.data.employees) {
                    return data.data.employees || [];
                } else if (data.data && Array.isArray(data.data)) {
                    return data.data;
                } else if (Array.isArray(data)) {
                    return data;
                } else if (data.employees && Array.isArray(data.employees)) {
                    return data.employees;
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.responseMessage || errorData.message || 'Failed to search interviewers';
                toast.error(errorMessage);
            }
            return [];
        },
        enabled: !!debouncedInterviewerSearch && debouncedInterviewerSearch.trim().length > 0,
        staleTime: 0,
    });

    // Load default interviewers
    const loadDefaultInterviewers = useCallback(async () => {
        setIsLoadingDefaultInterviewers(true);
        try {
            const baseUrl = API_BASE_URL;
            const url = `${baseUrl}/hr/employees?page=1&limit=20&status=active`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                let employees = [];

                if (data.responseCode === 200 && data.data && data.data.employees) {
                    employees = data.data.employees;
                } else if (data.data && Array.isArray(data.data)) {
                    employees = data.data;
                } else if (Array.isArray(data)) {
                    employees = data;
                } else if (data.employees && Array.isArray(data.employees)) {
                    employees = data.employees;
                }

                setDefaultInterviewers(employees || []);
            } else {
                const errorData = await response.json().catch(() => ({}));
                toast.error(errorData.responseMessage || 'Failed to load default interviewers');
            }
        } catch (error) {
            setDefaultInterviewers([]);
            toast.error('Network error occurred while loading interviewers');
        } finally {
            setIsLoadingDefaultInterviewers(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadDefaultInterviewers();
        }
    }, [isOpen, loadDefaultInterviewers]);

    const isSearchingInterviewers = !!debouncedInterviewerSearch.trim();
    const dropdownInterviewers = isSearchingInterviewers ? (searchedInterviewersData || []) : defaultInterviewers;
    const isDropdownLoading = isSearchingInterviewers ? isLoadingInterviewerSearch : isLoadingDefaultInterviewers;

    // Fetch locations using React Query
    const { data: locationsData, isLoading: loadingLocations } = useQuery({
        queryKey: ['locations'],
        queryFn: async () => {
            try {
                const result = await locationService.getLocations();

                if (result.success && result.data) {
                    if (result.data.data?.locations && Array.isArray(result.data.data.locations)) {
                        return result.data.data.locations;
                    }
                    if (result.data.locations && Array.isArray(result.data.locations)) {
                        return result.data.locations;
                    }
                    if (Array.isArray(result.data)) {
                        return result.data;
                    }
                }
                return [];
            } catch (error) {
                toast.error('Failed to load locations');
                return [];
            }
        },
        staleTime: 0,
        retry: 2,
        enabled: isOpen,
    });

    const locations = locationsData || [];

    // Fetch interview round types
    const { data: roundTypesData, isLoading: loadingRounds } = useQuery({
        queryKey: ['interview-rounds'],
        queryFn: async () => {
            try {
                const result = await interviewService.getInterviewRounds();
                if (result.success && result.data) {
                    if (Array.isArray(result.data)) return result.data;
                    if (result.data.data && Array.isArray(result.data.data)) return result.data.data;
                }
                return [];
            } catch (error) {
                console.error('Failed to load interview rounds:', error);
                return [];
            }
        },
        enabled: isOpen,
    });

    const roundTypes = roundTypesData || [];

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            try {
                if (showCandidateDropdown && candidateDropdownRef.current && !candidateDropdownRef.current.contains(event.target)) {
                    setShowCandidateDropdown(false);
                }
                if (showInterviewerDropdown && interviewerDropdownRef.current && !interviewerDropdownRef.current.contains(event.target)) {
                    setShowInterviewerDropdown(false);
                }
                if (showLocationDropdown && locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
                    setShowLocationDropdown(false);
                }
            } catch (_) { }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showCandidateDropdown, showInterviewerDropdown]);

    // Initialize form with preselected candidate
    useEffect(() => {
        if (isOpen && preselectedCandidate) {
            setFormData(prev => ({
                ...prev,
                candidateId: preselectedCandidate.id || preselectedCandidate._id || '',
            }));
            setCandidateSearchTerm(
                preselectedCandidate.name ||
                `${preselectedCandidate.firstName || ''} ${preselectedCandidate.lastName || ''}`.trim() ||
                'Unknown'
            );
        }
    }, [isOpen, preselectedCandidate]);

    // Reset form when modal closes or update when editData changes
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                candidateId: '',
                interviewerId: '',
                interviewerIds: [],
                date: '',
                time: '',
                duration: 60,
                type: 'video',
                location: '',
                meetingLink: '',
                notes: '',
                roundType: '',
                showInterviewerToCandidate: true,
            });
            setErrors({});
            setCandidateSearchTerm('');
            setInterviewerSearchTerm('');
            setSelectedInterviewers([]);
            setShowCandidateDropdown(false);
            setShowInterviewerDropdown(false);
        } else if (editData) {
            // Parse scheduledAt if available
            let dateStr = '';
            let timeStr = '';
            if (editData.scheduledAt) {
                const sDate = new Date(editData.scheduledAt);
                dateStr = sDate.toISOString().split('T')[0];
                timeStr = sDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
            }

            // Extract interviewers array or fallback to single interviewer
            const editInterviewersList = Array.isArray(editData.interviewers) && editData.interviewers.length > 0
                ? editData.interviewers
                : (editData.interviewer ? [editData.interviewer] : []);

            const extractedIds = editInterviewersList.map(i => i.id || i._id || i).filter(Boolean);
            const extractedInterviewerObjs = editInterviewersList.map(i => ({
                _id: i.id || i._id || i,
                id: i.id || i._id || i,
                name: i.name || `${i.firstName || ''} ${i.lastName || ''}`.trim() || 'Interviewer',
                personalInfo: {
                    firstName: i.name ? i.name.split(' ')[0] : i.firstName || '',
                    lastName: i.name ? i.name.split(' ').slice(1).join(' ') : i.lastName || ''
                },
                contactInfo: { email: i.email || '' },
                employmentInfo: { position: i.role || '', department: i.department || '' }
            }));

            // Populate form with edit data
            setFormData({
                candidateId: editData.candidate?.id || editData.candidateId || '',
                interviewerId: extractedIds[0] || '',
                interviewerIds: extractedIds,
                date: dateStr || editData.date || '',
                time: timeStr || editData.time || '',
                duration: editData.duration || 60,
                type: editData.type || 'video',
                location: editData.location || '',
                meetingLink: editData.meetingLink || '',
                notes: editData.notes || '',
                roundType: editData.roundType || '',
                showInterviewerToCandidate: editData.showInterviewerToCandidate !== undefined
                    ? editData.showInterviewerToCandidate
                    : (editData.rounds?.[0]?.showInterviewerToCandidate !== undefined ? editData.rounds[0].showInterviewerToCandidate : true),
            });

            // Set search terms for candidate
            setCandidateSearchTerm(editData.candidate?.name || editData.candidateName || '');
            setSelectedInterviewers(extractedInterviewerObjs);
        }
    }, [isOpen, editData]);

    // Fetch full interviewer details when editing
    useEffect(() => {
        const fetchInterviewerDetails = async () => {
            const primaryId = editData?.interviewers?.[0]?.id || editData?.interviewers?.[0] || editData?.interviewer?.id || editData?.interviewerId;
            if (isOpen && editData && primaryId) {
                try {
                    const baseUrl = API_BASE_URL;
                    const url = `${baseUrl}/hr/employees/${primaryId}`;
                    const response = await fetch(url, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                        }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        const fetchedData = data.data || data;
                        if (fetchedData) {
                            setSelectedInterviewers(prev => {
                                if (prev.length === 0) return [fetchedData];
                                return prev.map((item, idx) => idx === 0 ? { ...item, ...fetchedData } : item);
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error fetching interviewer details', error);
                }
            }
        };

        fetchInterviewerDetails();
    }, [isOpen, editData]);

    // Filter candidates based on search term
    const filteredCandidates = candidates.filter(candidate => {
        if (!candidateSearchTerm) return true;
        const searchLower = candidateSearchTerm.toLowerCase();
        const name = (candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()).toLowerCase();
        const email = (candidate.email || '').toLowerCase();
        const jobTitle = (candidate.jobTitle || '').toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower) || jobTitle.includes(searchLower);
    });

    // Validation functions
    const getValidationMessage = (name, value) => {
        let msg = '';
        const allowedDurations = [30, 45, 60, 90, 120];
        const isUrl = (u) => {
            try { new URL(u); return true; } catch { return false; }
        };

        switch (name) {
            case 'candidateId':
                if (!value) msg = 'Candidate is required.';
                break;
            case 'interviewerId':
            case 'interviewerIds':
                if (!formData.interviewerIds || formData.interviewerIds.length === 0) {
                    msg = 'At least one interviewer is required.';
                }
                break;
            case 'roundType':
                if (!value) msg = 'Interview Round is required.';
                break;
            case 'date': {
                if (!value) { msg = 'Date is required.'; break; }
                const d = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (d < today) msg = 'Interview date cannot be in the past.';
                break;
            }
            case 'time':
                if (!value) msg = 'Time is required.';
                break;
            case 'duration': {
                const dur = parseInt(value, 10);
                if (!allowedDurations.includes(dur)) msg = 'Invalid duration selected.';
                break;
            }
            case 'type':
                if (!value) msg = 'Interview type is required.';
                break;
            case 'meetingLink':
                if (formData.type === 'video') {
                    if (!value) msg = 'Meeting link is required for video interviews.';
                    else if (value.length > 300) msg = 'Meeting link is too long (max 300 chars).';
                    else if (!isUrl(value)) msg = 'Please enter a valid URL.';
                }
                break;
            case 'location':
                if (formData.type === 'in_person') {
                    if (!value || value.trim().length < 2) msg = 'Location is required for in-person interviews.';
                }
                break;
            case 'notes': {
                if (value && value.length > 3000) msg = 'Notes must be ≤ 3000 characters.';
                break;
            }
            default:
                break;
        }
        return msg;
    };

    const validateFieldInternal = (name, value) => {
        const msg = getValidationMessage(name, value);
        setErrors(prev => ({ ...prev, [name]: msg || undefined }));
        return msg;
    };

    const validateField = externalValidateField || validateFieldInternal;

    const validateForm = () => {
        const fieldsToValidate = ['candidateId', 'interviewerIds', 'roundType', 'date', 'time', 'duration', 'type', 'location', 'meetingLink', 'notes'];
        const nextErrors = {};

        fieldsToValidate.forEach(f => {
            const msg = getValidationMessage(f, formData[f]);
            if (msg) nextErrors[f] = msg;
        });

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name } = e.target;
        let { value } = e.target;

        // Date validation - prevent past dates
        if (name === 'date' && value) {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                setErrors(prev => ({ ...prev, date: 'Interview date cannot be in the past.' }));
                return;
            }
        }

        // Enforce character limit for meetingLink
        if (name === 'meetingLink' && value.length > 300) {
            value = value.slice(0, 300);
        }

        // Enforce character limit for notes
        if (name === 'notes' && value.length > 3000) {
            value = value.slice(0, 3000);
        }

        // Clear location/meetingLink when interview type changes
        const updateData = { [name]: value };
        const errorUpdates = { [name]: undefined };

        if (name === 'type') {
            if (value === 'video') {
                updateData.location = '';
                errorUpdates.location = undefined;
            } else if (value === 'in_person') {
                updateData.meetingLink = '';
                errorUpdates.meetingLink = undefined;
            }
        }

        setFormData(prev => ({
            ...prev,
            ...updateData,
        }));

        setErrors(prev => ({ ...prev, ...errorUpdates }));
    };

    const handleToggleInterviewer = (employee) => {
        const userId = employee.user?._id || employee.user || employee._id || employee.id;
        
        const isAlreadySelected = formData.interviewerIds.includes(userId);

        let newIds = [];
        let newObjs = [];

        if (isAlreadySelected) {
            newIds = formData.interviewerIds.filter(id => id !== userId);
            newObjs = selectedInterviewers.filter(item => (item.user?._id || item.user || item._id || item.id) !== userId);
        } else {
            newIds = [...formData.interviewerIds, userId];
            newObjs = [...selectedInterviewers, employee];
        }

        setFormData(prev => ({
            ...prev,
            interviewerId: newIds[0] || '',
            interviewerIds: newIds,
        }));
        setSelectedInterviewers(newObjs);

        setErrors(prev => ({ ...prev, interviewerIds: newIds.length > 0 ? undefined : 'At least one interviewer is required.' }));
    };

    const handleRemoveInterviewer = (userId) => {
        const newIds = formData.interviewerIds.filter(id => id !== userId);
        const newObjs = selectedInterviewers.filter(item => (item.user?._id || item.user || item._id || item.id) !== userId);

        setFormData(prev => ({
            ...prev,
            interviewerId: newIds[0] || '',
            interviewerIds: newIds,
        }));
        setSelectedInterviewers(newObjs);

        setErrors(prev => ({ ...prev, interviewerIds: newIds.length > 0 ? undefined : 'At least one interviewer is required.' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (loading) {
            return;
        }

        setLoading(true);

        const ok = validateForm();
        if (!ok) {
            setLoading(false);
            return;
        }

        try {
            let candidate = candidates.find((c) => c.id === formData.candidateId || c._id === formData.candidateId);

            // If not found in current candidates list but we are editing, use editData
            if (!candidate && editData && (
                editData.candidateId === formData.candidateId || 
                editData.candidate?._id === formData.candidateId ||
                editData.candidate?.id === formData.candidateId
            )) {
                candidate = {
                    id: editData.candidate?.id || editData.candidate?._id || editData.candidateId,
                    _id: editData.candidate?._id || editData.candidate?.id || editData.candidateId,
                    name: editData.candidateName || editData.candidate?.name,
                    jobId: editData.jobId,
                    jobTitle: editData.jobTitle,
                    job: editData.job
                };
            }

            if (!candidate) {
                throw new Error('Please select a candidate');
            }

            if (selectedInterviewers.length === 0 || !formData.interviewerIds || formData.interviewerIds.length === 0) {
                throw new Error('Please select at least one interviewer');
            }

            // Date validation
            const selectedDate = new Date(formData.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                throw new Error('Interview date cannot be in the past. Please select a future date.');
            }

            // Determine the interviewer values to send
            const interviewerValues = selectedInterviewers.map(interviewer => {
                if (interviewer.user?._id) return interviewer.user._id;
                if (interviewer.user) return interviewer.user;
                if (interviewer._id && String(interviewer._id).match(/^[0-9a-fA-F]{24}$/)) return interviewer._id;
                if (interviewer.id && String(interviewer.id).match(/^[0-9a-fA-F]{24}$/)) return interviewer.id;
                if (interviewer.employeeId) return interviewer.employeeId;
                return interviewer._id || interviewer.id;
            }).filter(Boolean);

            // Extract job ID from candidate
            const jobId =
                (candidate.jobId && candidate.jobId !== 'unknown' && candidate.jobId !== 'Not specified' ? candidate.jobId : null) ||
                (candidate.job?._id || candidate.job?.id) ||
                (typeof candidate.job === 'string' && !['unknown', 'Not specified', '[object Object]'].includes(candidate.job) ? candidate.job : null) ||
                (candidate.jobTitle && candidate.jobTitle !== 'Not specified' ? candidate.jobTitle : null) ||
                'unknown';

            const interviewData = {
                candidate: formData.candidateId,  // Backend expects 'candidate' not 'candidateId'
                job: jobId,  // Backend requires job field
                interviewer: interviewerValues[0],  // Backend fallback single interviewer
                interviewers: interviewerValues,  // Array of panel members
                interviewType: formData.type,
                scheduledDate: formData.date,
                scheduledTime: formData.time,
                duration: parseInt(formData.duration, 10),
                location: formData.type === 'in_person' ? formData.location : '',
                meetingLink: formData.type === 'video' ? formData.meetingLink : '',
                roundType: formData.roundType,
                showInterviewerToCandidate: formData.showInterviewerToCandidate,
                extraNote: formData.notes || '',
                notes: formData.notes || '',
                createdBy: editData?.createdBy || user?.id || user?._id,
            };


            let result;
            if (editData) {
                result = await interviewService.updateInterview(editData.id, interviewData);
            } else {
                result = await interviewService.scheduleInterview(interviewData);
            }

            if (result && (result.success || result.responseCode === 201 || result.responseCode === 200 || result?.data?.id || (result.responseMessage && result.responseMessage.toLowerCase().includes('successfully')))) {
                toast.success(editData ? 'Interview updated successfully!' : 'Interview scheduled successfully!');
                if (onSuccess) {
                    onSuccess(result);
                }
                onClose();
            } else {
                const errorMessage = 
                    result?.responseMessage || 
                    result?.message || 
                    result?.error || 
                    (result?.data && typeof result.data === 'string' ? result.data : null) ||
                    (result?.data?.responseMessage) ||
                    'Failed to schedule interview';
                    
                toast.error(errorMessage);
                return;
            }
        } catch (err) {
            const responseError = 
                err.responseData?.responseMessage || 
                err.response?.data?.responseMessage || 
                err.response?.data?.error ||
                err.message;
                
            toast.error(responseError || 'Failed to schedule interview. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-gray-900">
                            {editData ? 'Edit Interview' : 'Schedule Interview'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <XCircle className="h-6 w-6" />
                        </button>
                    </div>

                    <form noValidate onSubmit={handleSubmit} className="space-y-4">
                        {/* Round Name Field - Moved to Top */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Interview Round <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="roundType"
                                value={formData.roundType}
                                onChange={handleInputChange}
                                onBlur={() => validateField('roundType', formData.roundType)}
                                className={`w-full px-3 py-2 border rounded-lg outline-none focus:ring-0 focus:border-brand ${errors.roundType ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            >
                                <option value="">Select Round</option>
                                {roundTypes.length > 0 ? (
                                    roundTypes.filter(r => r.isActive !== false).map((round) => (
                                        <option key={round._id} value={round.name || round.roundName}>
                                            {round.name || round.roundName}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No rounds configured</option>
                                )}
                            </select>
                            {errors.roundType && (
                                <p className="mt-1 text-sm text-red-600">{errors.roundType}</p>
                            )}
                        </div>

                        {/* Section 1: Candidate & Panel Selection */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                            {/* Candidate Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Candidate <span className="text-red-500">*</span>
                                </label>
                                <div className="relative candidate-dropdown" ref={candidateDropdownRef}>
                                    <input
                                        type="text"
                                        placeholder={editData ? "" : `Search candidate... (${candidates.length} available)`}
                                        value={candidateSearchTerm}
                                        maxLength={150}
                                        disabled={!!editData}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const filteredValue = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 150);
                                            setCandidateSearchTerm(filteredValue);
                                            setShowCandidateDropdown(true);

                                            if (formData.candidateId) {
                                                setFormData(prev => ({ ...prev, candidateId: '' }));
                                            }
                                            validateField('candidateId', '');
                                        }}
                                        onBlur={() => validateField('candidateId', formData.candidateId)}
                                        onFocus={() => !editData && setShowCandidateDropdown(true)}
                                        className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${editData ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-700' : 'border-gray-300'
                                            }`}
                                    />
                                    {showCandidateDropdown && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {filteredCandidates.length > 0 ? (
                                                filteredCandidates.map((candidate) => (
                                                    <div
                                                        key={candidate.id || candidate._id}
                                                        className="px-3 py-2 hover:bg-blue-100/60 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, candidateId: candidate.id || candidate._id }));
                                                            setCandidateSearchTerm(
                                                                candidate.name ||
                                                                `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() ||
                                                                'Unknown'
                                                            );
                                                            setShowCandidateDropdown(false);
                                                            validateField('candidateId', candidate.id || candidate._id);
                                                        }}
                                                    >
                                                        <div className="font-medium text-gray-900">
                                                            {candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Unknown'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {candidate.jobTitle || 'N/A'} • {(candidate.email || candidate.status || '').toString()}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="px-3 py-2 text-gray-500 text-sm">No candidates found</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {errors.candidateId && (
                                    <p className="mt-1 text-sm text-red-600">{errors.candidateId}</p>
                                )}
                                {formData.candidateId && (
                                    <div className="mt-2 p-2.5 bg-brand/10 rounded-lg border border-brand/20 min-w-0">
                                        <div className="text-xs min-w-0">
                                            <div className="flex items-center gap-1.5 mb-0.5">
                                                <div className="bg-brand/20 p-0.5 rounded-full">
                                                    <Check className="w-3 h-3 text-brand" />
                                                </div>
                                                <span className="font-semibold text-brand">Selected Candidate</span>
                                            </div>
                                            <div className="font-medium text-gray-900 truncate">
                                                {(() => {
                                                    const candidate = candidates.find(c => c.id === formData.candidateId || c._id === formData.candidateId);
                                                    if (candidate) {
                                                        return candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim();
                                                    }
                                                    return candidateSearchTerm || 'Selected Candidate';
                                                })()}
                                            </div>
                                            {(() => {
                                                const candidate = candidates.find(c => c.id === formData.candidateId || c._id === formData.candidateId);
                                                const email = candidate?.email || editData?.candidate?.email || editData?.candidateEmail;

                                                if (email) {
                                                    return (
                                                        <div className="text-gray-600 text-[11px] truncate">
                                                            {email}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Multi-Interviewer Field */}
                            <div className="min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Interviewers / Panel <span className="text-red-500">*</span>
                                    </label>
                                    {selectedInterviewers.length > 0 && (
                                        <span className="text-xs font-semibold text-brand bg-brand/10 px-2 py-0.5 rounded-full">
                                            {selectedInterviewers.length} Panelist{selectedInterviewers.length > 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                                <div className="relative interviewer-dropdown min-w-0" ref={interviewerDropdownRef}>
                                    <div
                                        onClick={() => {
                                            if (showInterviewerDropdown) {
                                                validateField('interviewerIds', formData.interviewerIds);
                                            } else {
                                                setInterviewerSearchTerm("");
                                            }
                                            setShowInterviewerDropdown(!showInterviewerDropdown);
                                        }}
                                        className={`w-full px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between gap-2 hover:border-gray-400 focus-within:border-brand min-h-[42px] max-h-28 overflow-y-auto scrollbar-thin ${errors.interviewerIds ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                                            }`}
                                    >
                                        <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                                            {selectedInterviewers.length > 0 ? (
                                                selectedInterviewers.map((emp) => {
                                                    const empId = emp.user?._id || emp.user || emp._id || emp.id;
                                                    const empName = emp.personalInfo?.firstName && emp.personalInfo?.lastName
                                                        ? `${emp.personalInfo.firstName} ${emp.personalInfo.lastName}`
                                                        : emp.name || 'Interviewer';

                                                    return (
                                                        <span
                                                            key={empId}
                                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-brand/10 text-brand border border-brand/20 max-w-[130px] truncate"
                                                            title={empName}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <span className="truncate">{empName}</span>
                                                            <button
                                                                type="button"
                                                                className="hover:text-red-600 rounded-full p-0.5 flex-shrink-0 transition-colors"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveInterviewer(empId);
                                                                }}
                                                            >
                                                                <XCircle className="w-3.5 h-3.5" />
                                                            </button>
                                                        </span>
                                                    );
                                                })
                                            ) : (
                                                <span className="text-gray-400 text-sm">Select panel members...</span>
                                            )}
                                        </div>
                                        <ChevronDown
                                            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showInterviewerDropdown ? "transform rotate-180" : ""
                                                }`}
                                        />
                                    </div>

                                    {showInterviewerDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-72 overflow-hidden min-w-0">
                                            <div className="p-2 border-b border-gray-200 bg-gray-50">
                                                <div className="relative">
                                                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search employee by name..."
                                                        value={interviewerSearchTerm}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const filteredValue = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 150);
                                                            setInterviewerSearchTerm(filteredValue);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        maxLength={150}
                                                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-md outline-none focus:border-brand text-xs"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>

                                            <div className="max-h-56 overflow-y-auto min-w-0 divide-y divide-gray-100">
                                                {isDropdownLoading ? (
                                                    <div className="p-4 text-center text-gray-500 text-sm">
                                                        {isSearchingInterviewers ? 'Searching...' : 'Loading employees...'}
                                                    </div>
                                                ) : dropdownInterviewers.length > 0 ? (
                                                    dropdownInterviewers.map((employee) => {
                                                        const personalInfo = employee.personalInfo || {};
                                                        const employmentInfo = employee.employmentInfo || {};
                                                        const employeeName = personalInfo.firstName && personalInfo.lastName
                                                            ? `${personalInfo.firstName} ${personalInfo.lastName}`
                                                            : employee.name || 'Unknown';
                                                        const employeeDepartment = employmentInfo.department || employee.department || 'General';
                                                        const employeeRole = employmentInfo.position || employee.position || formatRoleName(employee.user?.role || employee.role);
                                                        const empUserId = employee.user?._id || employee.user || employee._id || employee.id;
                                                        const isSelected = formData.interviewerIds.includes(empUserId);

                                                        return (
                                                            <div
                                                                key={employee._id || employee.id}
                                                                onClick={() => handleToggleInterviewer(employee)}
                                                                className={`px-3 py-2 hover:bg-blue-50 cursor-pointer flex items-center justify-between text-xs transition-colors ${isSelected ? "bg-brand/5 font-semibold text-brand" : "text-gray-700"
                                                                    }`}
                                                            >
                                                                <div className="min-w-0 flex-1 pr-2">
                                                                    <div className="font-medium text-gray-900 truncate">
                                                                        {employeeName}
                                                                    </div>
                                                                    <div className="text-[11px] text-gray-500 truncate">
                                                                        {employeeRole} • {employeeDepartment}
                                                                    </div>
                                                                </div>
                                                                {isSelected ? (
                                                                    <span className="flex items-center gap-1 text-[11px] font-bold text-brand bg-brand/10 px-2 py-0.5 rounded-full flex-shrink-0">
                                                                        <Check className="w-3 h-3" /> Selected
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-[11px] text-gray-400 hover:text-brand flex-shrink-0">
                                                                        + Select
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="p-4 text-center text-gray-500 text-xs">
                                                        No employees found
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {errors.interviewerIds && (
                                    <p className="mt-1 text-sm text-red-600">{errors.interviewerIds}</p>
                                )}
                            </div>
                        </div>

                        {/* Section 2: Date & Time */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Date Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    onBlur={(e) => validateField('date', e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                                />
                                {errors.date && (
                                    <p className="mt-1 text-sm text-red-600">{errors.date}</p>
                                )}
                            </div>

                            {/* Time Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Time <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="time"
                                    name="time"
                                    value={formData.time}
                                    onChange={handleInputChange}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    onBlur={(e) => validateField('time', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                                />
                                {errors.time && (
                                    <p className="mt-1 text-sm text-red-600">{errors.time}</p>
                                )}
                            </div>
                        </div>

                        {/* Section 3: Duration & Interview Type */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Duration Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Duration (minutes)
                                </label>
                                <select
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleInputChange}
                                    onBlur={(e) => validateField('duration', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                                >
                                    <option value={30}>30 minutes</option>
                                    <option value={45}>45 minutes</option>
                                    <option value={60}>1 hour</option>
                                    <option value={90}>1.5 hours</option>
                                    <option value={120}>2 hours</option>
                                </select>
                                {errors.duration && (
                                    <p className="mt-1 text-sm text-red-600">{errors.duration}</p>
                                )}
                            </div>

                            {/* Interview Type Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Interview Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    onBlur={(e) => validateField('type', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                                >
                                    {interviewTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                                {errors.type && (
                                    <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                                )}
                            </div>
                        </div>

                        {/* Location Field (conditional) */}
                        {formData.type === 'in_person' && (
                            <div className="relative" ref={locationDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Location <span className="text-red-500">*</span>
                                </label>
                                <div
                                    onClick={() => !loadingLocations && setShowLocationDropdown(!showLocationDropdown)}
                                    className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between cursor-pointer
                                        ${errors.location ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}
                                        ${loadingLocations ? 'opacity-70 cursor-not-allowed' : 'hover:border-gray-400'}
                                    `}
                                >
                                    <span className={`block truncate mr-2 ${!formData.location ? 'text-gray-500' : 'text-gray-900'}`}>
                                        {formData.location || (loadingLocations ? 'Loading locations...' : 'Select Location')}
                                    </span>
                                    <ChevronDown
                                        className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showLocationDropdown ? "transform rotate-180" : ""}`}
                                    />
                                </div>

                                {showLocationDropdown && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {locations.length > 0 ? (
                                            locations.map((location) => {
                                                const val = location.fullAddress || location.name;
                                                const hasAddress = !!location.fullAddress;

                                                return (
                                                    <div
                                                        key={location.id || location._id}
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, location: val }));
                                                            validateField('location', val);
                                                            setShowLocationDropdown(false);
                                                        }}
                                                        className={`px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0
                                                            ${formData.location === val ? 'bg-blue-50' : ''}
                                                        `}
                                                    >
                                                        <div className="font-medium text-gray-900 truncate">
                                                            {location.name}
                                                        </div>
                                                        {hasAddress && (
                                                            <div className="text-sm text-gray-500 overflow-x-auto whitespace-nowrap pb-1 scrollbar-thin">
                                                                {location.fullAddress}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="px-3 py-2 text-gray-500 text-sm">No locations available</div>
                                        )}
                                    </div>
                                )}
                                {errors.location && (
                                    <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                                )}
                            </div>
                        )}

                        {/* Meeting Link Field (conditional) */}
                        {formData.type === 'video' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Meeting Link <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="url"
                                    name="meetingLink"
                                    value={formData.meetingLink}
                                    maxLength={300}
                                    onChange={handleInputChange}
                                    onBlur={(e) => validateField('meetingLink', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                                    placeholder="https://zoom.us/j/..."
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Required for video interviews
                                </p>
                                {errors.meetingLink && (
                                    <p className="mt-1 text-sm text-red-600">{errors.meetingLink}</p>
                                )}
                            </div>
                        )}

                        {/* Notes Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notes <span className="text-xs text-gray-500">(optional)</span>
                            </label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                maxLength={3000}
                                onChange={handleInputChange}
                                onBlur={(e) => validateField('notes', e.target.value)}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                                placeholder="Additional notes or agenda for the interview..."
                            />
                            <div className="mt-1 flex justify-between text-xs text-gray-500">
                                <span>{Math.min(formData.notes?.length || 0, 3000)}/3000</span>
                            </div>
                            {errors.notes && (
                                <p className="mt-1 text-sm text-red-600">{errors.notes}</p>
                            )}
                        </div>

                        {/* Show Interviewer Name to Candidate Toggle */}
                        <div className="flex items-center justify-between p-3.5 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                            <div className="pr-4">
                                <label htmlFor="showInterviewerToCandidate" className="text-sm font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                                    <User className="w-4 h-4 text-brand" />
                                    <span>Show Interviewer Name to Candidate in Email</span>
                                </label>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    When enabled, candidate will see interviewer name(s) in their email notifications. If unchecked, interviewer details will be omitted from the candidate email.
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                                <input
                                    type="checkbox"
                                    id="showInterviewerToCandidate"
                                    name="showInterviewerToCandidate"
                                    checked={formData.showInterviewerToCandidate}
                                    onChange={(e) => setFormData(prev => ({ ...prev, showInterviewerToCandidate: e.target.checked }))}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
                            </label>
                        </div>



                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                type="button"
                                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                            <PermissionGate permission="recruitment" action={editData ? "update" : "create"}>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={loading}
                                >
                                    {loading ? (editData ? 'Updating...' : 'Scheduling...') : (editData ? 'Update Interview' : 'Schedule Interview')}
                                </Button>
                            </PermissionGate>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ScheduleInterviewModal;
